/**
 * Gallery Tab Component
 * Displays multimedia content in a grid layout with thumbnail cards
 *
 * Design: Editorial/Magazine aesthetic
 * - Content-focused with minimal chrome
 * - 9:16 aspect ratio cards for vertical video content
 * - Source badges, metrics overlay, hover effects
 */

import { API_BASE_URL } from '../config/settings';

export interface GalleryItem {
  id: string;
  thumbnailUrl: string;
  r2ThumbnailKey?: string;
  source: 'instagram' | 'youtube' | 'tiktok' | 'web';
  contentFormat: string;
  views?: number;
  likes?: number;
  profileId?: string;
  profileUsername?: string;
  createdAt: number;
  sourceUrl: string;
}

interface GalleryFilters {
  source?: string;
  profileId?: string;
}

export class GalleryTab {
  private items: GalleryItem[] = [];
  private isLoading = false;
  private hasMore = true;
  private offset = 0;
  private limit = 50;
  private filters: GalleryFilters = {};
  private observer: IntersectionObserver | null = null;

  // DOM elements
  private gridEl: HTMLElement;
  private loadingEl: HTMLElement;
  private emptyEl: HTMLElement;
  private sourceFilterEl: HTMLSelectElement;
  private profileFilterEl: HTMLSelectElement;

  constructor() {
    this.gridEl = document.getElementById('gallery-grid')!;
    this.loadingEl = document.getElementById('gallery-loading')!;
    this.emptyEl = document.getElementById('gallery-empty')!;
    this.sourceFilterEl = document.getElementById('gallery-source-filter') as HTMLSelectElement;
    this.profileFilterEl = document.getElementById('gallery-profile-filter') as HTMLSelectElement;

    this.setupEventListeners();
    this.setupIntersectionObserver();
  }

  private setupEventListeners(): void {
    // Filter change handlers
    this.sourceFilterEl?.addEventListener('change', () => {
      this.filters.source = this.sourceFilterEl.value || undefined;
      this.reset();
      this.load();
    });

    this.profileFilterEl?.addEventListener('change', () => {
      this.filters.profileId = this.profileFilterEl.value || undefined;
      this.reset();
      this.load();
    });

    // Sync button in empty state
    const syncBtn = document.getElementById('gallery-sync-btn');
    syncBtn?.addEventListener('click', () => {
      // Navigate to profiles view
      const event = new CustomEvent('navigate-to-profiles');
      window.dispatchEvent(event);
    });

    // Card click handler (delegated)
    this.gridEl?.addEventListener('click', (e) => {
      const card = (e.target as HTMLElement).closest('.gallery-item');
      if (card) {
        const id = card.getAttribute('data-id');
        if (id) {
          this.handleItemClick(id);
        }
      }
    });
  }

  private setupIntersectionObserver(): void {
    // Lazy load images and infinite scroll
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              this.observer?.unobserve(img);
            }
          }
        });
      },
      { rootMargin: '200px' }
    );
  }

  private reset(): void {
    this.items = [];
    this.offset = 0;
    this.hasMore = true;
    this.gridEl.innerHTML = '';
  }

  async load(): Promise<void> {
    if (this.isLoading || !this.hasMore) return;

    this.isLoading = true;
    this.loadingEl.classList.remove('hidden');
    this.emptyEl.classList.add('hidden');

    try {
      const params = new URLSearchParams();
      params.set('limit', String(this.limit));
      params.set('offset', String(this.offset));
      if (this.filters.source) params.set('source', this.filters.source);
      if (this.filters.profileId) params.set('profileId', this.filters.profileId);

      const response = await fetch(`${API_BASE_URL}/multimedia/gallery?${params.toString()}`);
      const json = await response.json();

      if (json.success && json.data) {
        const newItems: GalleryItem[] = json.data.items;
        this.items.push(...newItems);
        this.offset += newItems.length;
        this.hasMore = newItems.length === this.limit;

        this.renderItems(newItems);

        // Show empty state if no items
        if (this.items.length === 0) {
          this.emptyEl.classList.remove('hidden');
        }
      }
    } catch (error) {
      console.error('Failed to load gallery:', error);
    } finally {
      this.isLoading = false;
      this.loadingEl.classList.add('hidden');
    }
  }

  private renderItems(items: GalleryItem[]): void {
    const fragment = document.createDocumentFragment();

    items.forEach((item) => {
      const card = this.createCard(item);
      fragment.appendChild(card);
    });

    this.gridEl.appendChild(fragment);
  }

  private createCard(item: GalleryItem): HTMLElement {
    const card = document.createElement('div');
    card.className = 'gallery-item';
    card.setAttribute('data-id', item.id);

    // Source badge colors
    const sourceColors: Record<string, string> = {
      instagram: 'rgba(225, 48, 108, 0.9)',
      youtube: 'rgba(255, 0, 0, 0.9)',
      tiktok: 'rgba(0, 0, 0, 0.9)',
      web: 'rgba(107, 114, 128, 0.9)'
    };

    const sourceLabels: Record<string, string> = {
      instagram: 'IG',
      youtube: 'YT',
      tiktok: 'TT',
      web: 'WEB'
    };

    // Format numbers
    const formatNumber = (num?: number): string => {
      if (!num) return '';
      if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
      if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
      return String(num);
    };

    card.innerHTML = `
      <img
        class="gallery-item-img"
        data-src="${item.thumbnailUrl}"
        src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 9 16'%3E%3Crect fill='%23f3f4f6' width='9' height='16'/%3E%3C/svg%3E"
        alt=""
        loading="lazy"
      />
      <div class="gallery-item-source" style="background: ${sourceColors[item.source] || sourceColors.web}">
        ${sourceLabels[item.source] || 'WEB'}
      </div>
      <div class="gallery-item-overlay">
        <div class="gallery-item-metrics">
          ${item.views ? `<span class="gallery-item-metric"><span class="material-symbols-outlined">visibility</span>${formatNumber(item.views)}</span>` : ''}
          ${item.likes ? `<span class="gallery-item-metric"><span class="material-symbols-outlined">favorite</span>${formatNumber(item.likes)}</span>` : ''}
        </div>
        ${item.profileUsername ? `<div class="gallery-item-username">@${item.profileUsername}</div>` : ''}
      </div>
    `;

    // Setup lazy loading for this image
    const img = card.querySelector('img');
    if (img && this.observer) {
      this.observer.observe(img);
    }

    return card;
  }

  private handleItemClick(id: string): void {
    const item = this.items.find((i) => i.id === id);
    if (item) {
      // Open source URL in new tab
      window.open(item.sourceUrl, '_blank');
    }
  }

  async loadProfiles(): Promise<void> {
    // Populate profile filter dropdown
    try {
      const response = await fetch(`${API_BASE_URL}/profiles?limit=100`);
      const json = await response.json();

      if (json.success && json.data?.profiles) {
        const profiles = json.data.profiles;

        // Clear existing options except "All Profiles"
        while (this.profileFilterEl.options.length > 1) {
          this.profileFilterEl.remove(1);
        }

        // Add profile options
        profiles.forEach((profile: { id: string; username: string; platform: string }) => {
          const option = document.createElement('option');
          option.value = profile.id;
          option.textContent = `@${profile.username} (${profile.platform})`;
          this.profileFilterEl.appendChild(option);
        });
      }
    } catch (error) {
      console.error('Failed to load profiles for filter:', error);
    }
  }

  render(): void {
    // Called when tab becomes visible
    if (this.items.length === 0) {
      this.loadProfiles();
      this.load();
    }
  }

  destroy(): void {
    this.observer?.disconnect();
  }
}
